import { Button, Col, DatePicker, DatePickerProps, Form, InputNumber, Row, Table, Tag } from 'antd';
import Column from 'antd/es/table/Column';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { ContentHeader } from '../../components';
import ModalTextForm from '../../components/modal/ModalTextForm';
import { Links } from '../../helpers/Links';
import { dateCommon, formatVND, validateMessages } from '../../helpers/common';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';
import { khoiTaoLuongThemGio, timKiemDsLuongThemGio } from '../../services/luong';
import { LuongThemGioInfo } from '../../models/luong/LuongThemGio';

const DanhSachLuongThemGio = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [dataDs, setDataDs] = useState<LuongThemGioInfo[]>([]);
    const [ngayThang, setNgayThang] = useState<Dayjs | null>(null);
    const [openModalKhoiTao, setOpenModalKhoiTao] = useState(false);

    const layDuLieu = async (thangNam?: string) => {
        setLoading(true);
        try {
            const rs = await timKiemDsLuongThemGio(0, 1000, thangNam);
            setDataDs(rs?.data ?? []);
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        setLoading(false);
    };

    useEffect(() => {
        layDuLieu(ngayThang ? ngayThang.format('MM/YYYY') : undefined);
    }, []);

    const monthFormat = 'MM/YYYY';
    const thangNamFormat: DatePickerProps['format'] = (value) => `Tháng ${value.format(monthFormat)}`;

    const onChangeThangNam: DatePickerProps['onChange'] = (date) => {
        setNgayThang(date);
        layDuLieu(date ? date.format('MM/YYYY') : undefined);
    };

    const onMoModalKhoiTao = async () => {
        const ky = ngayThang ?? dayjs();
        form.setFieldsValue({
            kyLuong: ky,
        });
        setOpenModalKhoiTao(true);
    };

    const onFinishKhoiTao = async (values: any) => {
        dispatch(showLoadingScreen());
        try {
            const ky = values?.kyLuong as Dayjs;
            const request = {
                thangNam: ky.format('MM/YYYY'),
            };
            const rs = await khoiTaoLuongThemGio(request);
            if (rs > 0) {
                toast.success('Khởi tạo lương thêm giờ thành công!');
                setOpenModalKhoiTao(false);
                layDuLieu(ngayThang ? ngayThang.format('MM/YYYY') : undefined);
            } else {
                toast.error('Khởi tạo lương thêm giờ thất bại!');
            }
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    return (
        <section>
            {openModalKhoiTao && (
                <ModalTextForm
                    title="Khởi tạo lương thêm giờ"
                    hideFooter
                    show={openModalKhoiTao}
                    handleClose={() => setOpenModalKhoiTao(false)}
                >
                    <Form
                        layout="vertical"
                        validateMessages={validateMessages}
                        onFinish={onFinishKhoiTao}
                        form={form}
                    >
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item rules={[{ required: true }]} label="Tháng năm" name="kyLuong">
                                    <DatePicker
                                        changeOnBlur={true}
                                        picker="month"
                                        format={["MM/YYYY", "MMYYYY"]}
                                        placeholder="Chọn kỳ lương"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Button
                                type="primary"
                                className="float-right"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                            >
                                Khởi tạo
                            </Button>
                        </Form.Item>
                    </Form>
                </ModalTextForm>
            )}

            <ContentHeader title="Lương thêm giờ" />

            <Row className="form-group" gutter={8}>
                <Col span={6}>
                    <DatePicker
                        changeOnBlur={true}
                        value={ngayThang}
                        onChange={onChangeThangNam}
                        placeholder="Tháng tính lương"
                        picker="month"
                        format={thangNamFormat}
                        style={{ width: '100%' }}
                    />
                </Col>
                <Col span={18}>
                    <Button
                        className="hrm-btn hrm-btn-warning float-right"
                        icon={<PlusOutlined />}
                        onClick={onMoModalKhoiTao}
                    >
                        Khởi tạo lương thêm giờ
                    </Button>
                </Col>
            </Row>

            <Row gutter={8}>
                <Col span={24}>
                    <div className="card card-primary">
                        <Row>
                            <Col span={24}>
                                <Table
                                    locale={{ emptyText: "Không có dữ liệu" }}
                                    dataSource={(dataDs || []).map((value, index) => ({ key: index, ...value }))}
                                    loading={loading}
                                >
                                    <Column
                                        title="STT"
                                        width={60}
                                        align="center"
                                        render={(_v, _r, index) => (
                                            <div className="text-align-middle">{index + 1}</div>
                                        )}
                                    />
                                    <Column
                                        title="Kỳ lương"
                                        key="tenKyLuong"
                                        align="left"
                                        render={(value: any) => (
                                            <div className="text-align-middle">{value?.tenKyLuong}</div>
                                        )}
                                    />
                                    {/*<Column
                                        title="Quỹ lương"
                                        key="quyLuong"
                                        align="center"
                                        render={(value: any) => (
                                            <div className="text-align-middle">
                                                {formatVND(value?.quyLuong ?? 0)}
                                            </div>
                                        )}
                                    />*/}
                                    <Column
                                        title="Trạng thái"
                                        key="trangThai"
                                        align="center"
                                        render={(value: any) => (
                                            value.trangThai == 0 ? (<Tag color="blue">Khởi tạo</Tag>) : (<Tag color="success">Đã xác nhận</Tag>)
                                        )}
                                    />
                                    <Column
                                        title="Người khởi tạo"
                                        key="tenNVKhoiTao"
                                        align="center"
                                        render={(value) => (
                                            <div className="text-align-middle">{value?.tenNVKhoiTao}</div>
                                        )}
                                    />
                                    <Column
                                        title="Ngày khởi tạo"
                                        key="ngayKhoiTao"
                                        align="center"
                                        render={(value) => (
                                            <div className="text-align-middle">{dateCommon.formatDate(value?.ngayKhoiTao)}</div>
                                        )}
                                    />
                                    <Column
                                        title=""
                                        width={120}
                                        key="action"
                                        align="center"
                                        render={(value: any, _record, index) => (
                                            <Link
                                                key={"link" + index}
                                                className="hrm-btn hrm-btn-sm btn-primary"
                                                to={`${Links.Luong.ChiTietLuongThemGio}?id=${value?.id}`}
                                            >
                                                Xem
                                            </Link>
                                        )}
                                    />
                                </Table>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default DanhSachLuongThemGio;